/**
 * Feasibility — per-type preferences sub-form (Workflow Prompt 2).
 *
 * SSOT — Site Project Intent is the canonical single source of truth for
 * MW / customer / in-service date / risk / power-source / cooling. Those
 * six fields no longer live on FeasibilityPreferences (deleted in
 * SSOT-3). The form now carries only the KPI weight overrides — the
 * single truly-feasibility-only knob. A summary block at the top of the
 * form directs users to the Site Project Intent editor for the shared
 * fields.
 *
 * Pack: docs/prompt-packs/Site_SSOT_Prompt.md
 */
// R-131-OVERVIEW-UX-A
import React from 'react';
import { Slider } from '../../ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import type { FeasibilityPreferences } from '../../../types/siteRag';
import { usePrefsValidation } from './shared';
import { AffectsScoreBadge } from './AffectsScoreBadge';
import { validateFeasibilityPreferences } from '../../../lib/reportPreferences/validation';
// Pure validator lives in lib so server code can use it without this form.
export { validateFeasibilityPreferences };

interface Props {
  value: FeasibilityPreferences | undefined;
  onChange: (next: FeasibilityPreferences) => void;
  /**
   * SSOT — opens the Site Project Intent editor when the user wants to
   * edit one of the deleted shared fields. Wired by callers (wizard /
   * Site Config Panel / rerun) to mount the canonical
   * `ProjectIntentForm` editor. Optional — when not wired, the SSOT
   * summary block still renders but the inline edit link is hidden.
   */
  onOpenSiteIntent?: () => void;
}

const KPI_KEYS = ['grid', 'cooling', 'site', 'regulatory', 'logistics'] as const;
type KpiKey = typeof KPI_KEYS[number];

const KPI_LABELS: Record<KpiKey, string> = {
  grid: 'Power',
  cooling: 'Cooling',
  site: 'Site',
  regulatory: 'Regulatory',
  logistics: 'Logistics',
};

const STANDARD_WEIGHTS: Record<KpiKey, number> = {
  grid: 0.30,
  cooling: 0.25,
  site: 0.15,
  regulatory: 0.20,
  logistics: 0.10,
};

export const FeasibilityPreferencesForm: React.FC<Props> = ({ value: rawValue, onChange, onOpenSiteIntent }) => {
  const value: FeasibilityPreferences = (rawValue ?? {}) as FeasibilityPreferences;

  const setWeight = (key: KpiKey, n: number) => {
    onChange({ ...value, kpiWeightOverrides: { ...(value.kpiWeightOverrides ?? {}), [key]: n } });
  };

  const usingCustomWeights = value.kpiWeightOverrides && Object.keys(value.kpiWeightOverrides).length > 0;
  const tabValue = usingCustomWeights ? 'custom' : 'standard';
  const weightSum = KPI_KEYS.reduce((s, k) => s + (value.kpiWeightOverrides?.[k] ?? 0), 0);

  // CU3/CU4 — surface any cross-field warning + score-affecting badge
  // for the KPI weights block.
  const prefsCtx = usePrefsValidation();
  const kpiWarning = prefsCtx.warningsByField['kpiWeightOverrides'];
  const kpiScoreAffecting = prefsCtx.scoreAffectingKeys.includes('kpiWeightOverrides');

  return (
    <div className="space-y-5">
      {/* SSOT — shared inputs live on Site Project Intent (the single
          source of truth). Edit them at site level; this form only
          carries report-type-specific overrides. */}
      <div
        data-testid="ssot-summary-feasibility"
        className="text-[10px] text-text-muted bg-state-hover border border-border px-3 py-2"
      >
        Workload profile, MW, in-service date, cooling, nuclear interest, and priority are set at{' '}
        <strong>Site Project Intent</strong>.{onOpenSiteIntent ? (
          <>{' '}
            <button
              type="button"
              onClick={onOpenSiteIntent}
              className="text-primary underline"
              data-testid="ssot-open-site-intent-feasibility"
            >
              Edit Site Project Intent →
            </button>
          </>
        ) : null}
      </div>

      <div
        className="border-t border-border pt-4"
        data-testid="pref-field-kpiWeightOverrides"
      >
        {kpiScoreAffecting && (
          <div className="mb-2 flex items-center gap-2">
            <AffectsScoreBadge fieldKey="kpiWeightOverrides" />
            <span className="text-sm font-semibold text-text-secondary">
              KPI weight overrides
            </span>
          </div>
        )}
        <Tabs
          value={tabValue}
          onValueChange={(v) => {
            if (v === 'standard') onChange({ ...value, kpiWeightOverrides: undefined });
            else if (v === 'custom' && !usingCustomWeights) {
              onChange({ ...value, kpiWeightOverrides: { ...STANDARD_WEIGHTS } });
            }
          }}
          data-testid="feasibility-kpi-weight-tabs"
        >
          <TabsList className="grid w-full max-w-[280px] grid-cols-2 h-8">
            <TabsTrigger value="standard">Standard weights</TabsTrigger>
            <TabsTrigger value="custom">Custom</TabsTrigger>
          </TabsList>
          <TabsContent value="standard" className="mt-3">
            <div
              className="rounded-md border border-border-subtle bg-background/40 p-3 text-sm text-text-secondary leading-relaxed"
              data-testid="feasibility-standard-weights-card"
            >
              Standard weights tuned for typical site profiles. Power 30% · Cooling 25% · Site 15% ·
              Regulatory 20% · Logistics 10%. Switch to Custom if your project weighs these differently.
            </div>
          </TabsContent>
          <TabsContent value="custom" className="space-y-3 mt-3">
            {KPI_KEYS.map((key) => {
              const w = value.kpiWeightOverrides?.[key] ?? 0;
              return (
                <div key={key}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-text-secondary">{KPI_LABELS[key]}</span>
                    <span className="font-mono text-text-primary">{w.toFixed(2)}</span>
                  </div>
                  <Slider
                    value={[w]}
                    min={0} max={1} step={0.05}
                    onValueChange={(arr) => setWeight(key, arr[0])}
                    className="mt-1"
                  />
                </div>
              );
            })}
            <div className={`text-[10px] mt-2 ${Math.abs(weightSum - 1) < 0.05 ? 'text-success' : 'text-warning'}`}>
              Sum: {weightSum.toFixed(2)} {Math.abs(weightSum - 1) < 0.05 ? '— balanced' : '— should sum to ~1.0'}
            </div>
          </TabsContent>
        </Tabs>
        {kpiWarning && (
          <div
            className={`type-body mt-2 ${kpiWarning.severity === 'error' ? 'text-danger' : 'text-warning'}`}
            data-testid="pref-warning-kpiWeightOverrides"
          >
            {kpiWarning.message}
          </div>
        )}
      </div>
    </div>
  );
};
